import { computed, inject, Injectable, Signal } from '@angular/core';
import { CorporateDesignStore } from '../api/corporate-design/corporate-design-store.service';
import { DarkModeStore } from '../api/dark-mode/dark-mode-store.service';
import { ICorporateDesign } from '@web/dtos';
import { SiteContextService } from '../site-context/site-context.service';

@Injectable({
  providedIn: 'root',
})
export class CorporateDesignService {
  private readonly siteContextService: SiteContextService =
    inject(SiteContextService);
  private readonly darkModeStore: DarkModeStore = inject(DarkModeStore);
  private readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);

  public corporateDesign: Signal<ICorporateDesign> = computed(
    (): ICorporateDesign => {
      return this.corporateDesignStore.corporateDesign();
    }
  );

  public isDarkModeActive: Signal<boolean> = computed(() => {
    return this.siteContextService.isShopContext()
      ? this.darkModeStore.isActive()
      : false;
  });

  isCorporateDesignLoading(): boolean {
    return this.corporateDesignStore.isLoading();
  }

  primaryColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().primaryDarkColor;
    }
    return this.corporateDesign().primaryColor;
  }

  primaryFontColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().primaryDarkFontColor;
    }
    return this.corporateDesign().primaryFontColor;
  }

  primaryBorderColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().primaryDarkBorderColor;
    }
    return this.corporateDesign().primaryBorderColor;
  }

  primaryOutlineColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().primaryDarkAlternativeColor;
    }
    return this.corporateDesign().primaryAlternativeColor;
  }

  secondaryColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().secondaryDarkColor;
    }
    return this.corporateDesign().secondaryColor;
  }

  secondaryFontColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().secondaryDarkFontColor;
    }
    return this.corporateDesign().secondaryFontColor;
  }

  secondaryBorderColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().secondaryDarkBorderColor;
    }
    return this.corporateDesign().secondaryBorderColor;
  }

  secondaryOutlineColor(): string {
    if(!this.siteContextService.isShopContext()){
      return '';
    }

    if (this.isDarkModeActive()) {
      return this.corporateDesign().secondaryDarkAlternativeColor;
    }
    return this.corporateDesign().secondaryAlternativeColor;
  }

  baseFont(): string {
    return this.isDarkModeActive() ? 'text-standard-text-dark' : 'text-standard-text';
  }

  darkFontColor(lightMode: string): string {
    if (this.darkModeStore.isActive()) {
      return this.corporateDesignStore.corporateDesign().primaryDarkFontColor;
    }
    return lightMode;
  }

  fontColor(): string | undefined {
    return this.siteContextService.isShopContext() && this.isDarkModeActive()
      ? this.corporateDesign().primaryDarkFontColor
      : undefined;
  }

  secondaryDarkFontColor(lightMode: string): string {
    if (this.darkModeStore.isActive()) {
      return this.corporateDesign().colorAdmixtures.dark_text;
    }
    return lightMode;
  }

  linkIconColor(): string {
    if (this.darkModeStore.isActive()) {
      return this.corporateDesignStore.corporateDesign().linkIconDark;
    }
    return this.corporateDesignStore.corporateDesign().linkIcon;
  }

  borderRadius(): string | undefined {
    if (this.siteContextService.isShopContext()) {
      return this.corporateDesign().buttonBorderRounding + 'px';
    }
    return undefined;
  }

  surface1(): string {
    return this.isDarkModeActive()
      ? this.corporateDesign().colorAdmixtures.dark_surface_01
      : '#fff';
  }
}
